import React, { useDeferredValue, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Autocomplete, Box, Grid, TextField, Typography } from "@mui/material";
import ButtonComponent from "../../../components/common/button/Button";
import FilterMultiSelectExtractSAP from "../../../components/common/FilterMultiSelectExtractSAP";
import { postExtractForecastFromSAPSlice } from "../../../redux/actions/postExtractForecastFromSAPSlice";
import { postExtractForecastFromSAPEndpoint } from "../../../common/endpoints";

const filterConfig = [
  {
    key: "mrpArea",
    label: "MRP Area",
  },
  {
    key: "tccMaterialNumber",
    label: "Material",
    codeField: "tccMaterialNumber",
    nameField: "ingredientName",
  },
  {
    key: "mrpController",
    label: "MRP Controller",
    codeField: "mrpController",
    nameField: "mrpControllerName",
  },
  {
    key: "purchasingGroup",
    label: "Purchasing Group",
    codeField: "purchasingGroup",
    nameField: "purchasingGroupDesc",
  },
  {
    key: "plantSpecificMaterialStatus",
    label: "Plant Specific Material Status",
  },
  {
    key: "externalMaterialGroup",
    label: "External Material Group",
  },
  {
    key: "supplierPIN",
    label: "Supplier PIN",
    codeField: "supplierPIN",
    nameField: "supplierName",
  },
];

const vendorFilterConfig = [
  {
    key: "vendorCode",
    label: "Vendor",
    codeField: "vendor",
    nameField: "vendorName",
  },
  {
    key: "parentSupplierID",
    label: "Parent Supplier ID",
    codeField: "parentVendor",
    nameField: "supplierName",
  },
];

const normalizeFilterValue = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

const FILTER_KEYS = filterConfig.map((item) => item.key);

// CREATE OPTIONS
const getUniqueOptions = (data, config) => {
  const uniqueMap = new Map();

  data.forEach((row, index) => {
    // CODE BASED DROPDOWNS
    if (config.codeField && config.nameField) {
      const code = row[config.codeField];

      const name = row[config.nameField];

      const extra = config.extraField ? row[config.extraField] : null;

      // IGNORE IF EVERYTHING EMPTY
      const isEmpty = (val) => val === null || val === undefined || val === "";

      if (isEmpty(code) && isEmpty(name) && isEmpty(extra)) {
        return;
      }

      // LABEL PARTS
      const parts = [];

      if (code) {
        parts.push(code);
      }

      if (name) {
        parts.push(name);
      }

      if (extra) {
        parts.push(extra);
      }

      const label = parts.join(" | ");

      // UNIQUE KEY
      const uniqueKey = code || `${name}_${extra}_${index}`;

      uniqueMap.set(uniqueKey, {
        label,
        value: uniqueKey,
        actualValue: code,
      });
    }

    // NORMAL DROPDOWNS
    else {
      const value = row[config.key];

      // REMOVE EMPTY OPTIONS
      if (value == null || value === "") {
        return;
      }

      uniqueMap.set(value, {
        label: value,
        value: value,
      });
    }
  });

  return [...uniqueMap.values()];
};

export default function ExtractForecastFilterScreens({
  plantOptions,
  selectedPlant,
  onPlantChange,
  dropdownData,
  dropdownVendorData,
  plantLoading = false,
  dependentDropdownLoading = false,
  onRenderExecuteCallback,
  onRenderClearCallback,
  selectedTimePeriodFrom,
  selectedTimePeriodTo,
  sendEmailToPlanner,
}) {
  // INITIAL FILTERS

  const initialFilters = {};

  filterConfig?.forEach((item) => {
    initialFilters[item.key] = [];
  });
  vendorFilterConfig.forEach((item) => {
    initialFilters[item.key] = [];
  });
  const userEmail = localStorage.getItem("userEmail") || "";
  const userName = localStorage.getItem("userName") || "";
  const userRole = localStorage.getItem("userRole") || "";
  const selectedRegion = useSelector((state) => state.setRegionPlantStore.selectedRegion);
  const plantNameOnly = useSelector((state) => state.setRegionPlantStore.selectedPlant);
  const selectedPlantForPayload = plantNameOnly
    ? plantNameOnly.split(" - ")[0]
    : null;

  const [selectedFilters, setSelectedFilters] = useState(initialFilters);
  const [description, setDescription] = useState("");
  const dispatch = useDispatch();
  const executeLoading = useSelector(
    (state) => state.postExtractForecastFromSAPSlice.loading,
  );
  const selectedPlantCodes = selectedPlant?.actualValue
    ? [String(selectedPlant.actualValue).trim()]
    : [];

  const hasSelectedPlant = selectedPlantCodes.length > 0;
  const showDependentLoader = hasSelectedPlant && dependentDropdownLoading;

  const safeDropdownData = useMemo(
    () => (Array.isArray(dropdownData) ? dropdownData : []),
    [dropdownData],
  );
  // Complete response from parent
  const safeDropdownVendorData = useMemo(
    () => (Array.isArray(dropdownVendorData) ? dropdownVendorData : []),
    [dropdownVendorData]
  );

  // Separate arrays from API response
  const vendorList = useMemo(
    () => safeDropdownVendorData?.[0]?.vendorList || [],
    [safeDropdownVendorData]
  );

  const parentChildDetailsList = useMemo(
    () => safeDropdownVendorData?.[0]?.parentChildDetailsList || [],
    [safeDropdownVendorData]
  );

  const normalizedRows = useMemo(() => {
    return safeDropdownData.map((row) => {
      const normalized = {};

      FILTER_KEYS.forEach((key) => {
        normalized[key] = normalizeFilterValue(row?.[key] ?? "");
      });

      return {
        row,
        normalized,
      };
    });
  }, [safeDropdownData]);

  const staticDropdownOptions = useMemo(() => {
    const result = {};

    filterConfig.forEach((filter) => {
      result[filter.key] = getUniqueOptions(safeDropdownData, filter);
    });

    return result;
  }, [safeDropdownData]);

  const vendorStaticDropdownOptions = useMemo(() => {
    const result = {};

    vendorFilterConfig.forEach((filter) => {

      let source =
        filter.key === "vendorCode"
          ? vendorList
          : parentChildDetailsList;

      result[filter.key] = getUniqueOptions(source, filter)
        .sort((a, b) =>
          a.label.localeCompare(b.label)
        );
    });

    return result;
  }, [vendorList, parentChildDetailsList]);

  const vendorRelationshipIndex = useMemo(() => {
    const vendorCodesByParent = new Map();
    const parentCodesByVendor = new Map();

    parentChildDetailsList.forEach((row) => {
      const vendorCode = normalizeFilterValue(row.vendor);
      const parentCode = normalizeFilterValue(row.parentVendor);

      if (!vendorCode || !parentCode) {
        return;
      }

      if (!vendorCodesByParent.has(parentCode)) {
        vendorCodesByParent.set(parentCode, new Set());
      }
      vendorCodesByParent.get(parentCode).add(vendorCode);

      if (!parentCodesByVendor.has(vendorCode)) {
        parentCodesByVendor.set(vendorCode, new Set());
      }
      parentCodesByVendor.get(vendorCode).add(parentCode);
    });

    return { vendorCodesByParent, parentCodesByVendor };
  }, [parentChildDetailsList]);



  const selectedValueSets = useMemo(() => {
    const result = {};

    filterConfig.forEach(({ key }) => {
      const selected = selectedFilters[key] || [];
      const hasAllMarker = selected.some((item) => item?.__isAll);

      result[key] = {
        hasFilter: !hasAllMarker && selected.length > 0,
        values: new Set(
          selected
            .filter((item) => !item?.__isAll)
            .map((item) =>
              normalizeFilterValue(item.actualValue ?? item.value ?? ""),
            ),
        ),
      };
    });

    return result;
  }, [selectedFilters]);

  const vendorSelectedValueSets = useMemo(() => {

    const result = {};

    vendorFilterConfig.forEach(({ key }) => {

      const selected = selectedFilters[key] || [];

      const hasAllMarker = selected.some(item => item?.__isAll);

      result[key] = {

        hasFilter: !hasAllMarker && selected.length > 0,

        values: new Set(

          selected
            .filter(item => !item?.__isAll)
            .map(item =>
              normalizeFilterValue(
                item.actualValue ?? item.value
              )
            )

        )

      };

    });

    return result;

  }, [selectedFilters]);

  const deferredSelectedValueSets = useDeferredValue(selectedValueSets);
  const deferredVendorSelectedValueSets = useDeferredValue(vendorSelectedValueSets);

  // Cross-filter options: each dropdown is filtered by selections from other dropdowns.
  const dropdownOptions = useMemo(() => {
    if (!hasSelectedPlant || showDependentLoader) {
      const emptyResult = {};
      filterConfig.forEach((filter) => {
        emptyResult[filter.key] = [];
      });
      return emptyResult;
    }

    const result = {};
    const activeFilterCount = filterConfig.reduce((count, config) => {
      const selectionMeta = deferredSelectedValueSets[config.key];
      return selectionMeta?.hasFilter ? count + 1 : count;
    }, 0);

    if (activeFilterCount === 0) {
      return staticDropdownOptions;
    }

    filterConfig.forEach((filter) => {
      const hasOtherActiveFilters = filterConfig.some((config) => {
        if (config.key === filter.key) {
          return false;
        }

        return deferredSelectedValueSets[config.key]?.hasFilter;
      });

      if (!hasOtherActiveFilters) {
        result[filter.key] = staticDropdownOptions[filter.key] || [];
        return;
      }

      let tempRows = normalizedRows;

      filterConfig.forEach((config) => {
        if (config.key === filter.key) {
          return;
        }

        const selectionMeta = deferredSelectedValueSets[config.key];
        if (!selectionMeta?.hasFilter || selectionMeta.values.size === 0) {
          return;
        }

        tempRows = tempRows.filter((item) =>
          selectionMeta.values.has(item.normalized[config.key]),
        );
      });

      result[filter.key] = getUniqueOptions(
        tempRows.map((item) => item.row),
        filter,
      );
    });

    return result;
  }, [
    normalizedRows,
    hasSelectedPlant,
    showDependentLoader,
    deferredSelectedValueSets,
    staticDropdownOptions,
  ]);

  const vendorDropdownOptions = useMemo(() => {

    const result = {};

    const selectedVendors =
      deferredVendorSelectedValueSets.vendorCode?.values || new Set();

    const selectedParents =
      deferredVendorSelectedValueSets.parentSupplierID?.values || new Set();

    // -------------------------
    // Vendor Dropdown
    // -------------------------
    if (selectedParents.size > 0) {
      const vendorCodes = new Set();

      selectedParents.forEach((parentCode) => {
        vendorRelationshipIndex.vendorCodesByParent
          .get(parentCode)
          ?.forEach((vendorCode) => vendorCodes.add(vendorCode));
      });

      result.vendorCode = vendorStaticDropdownOptions.vendorCode.filter(
        (option) => vendorCodes.has(
          normalizeFilterValue(option.actualValue ?? option.value)
        )
      );

    } else {

      result.vendorCode =
        vendorStaticDropdownOptions.vendorCode;

    }

    // -------------------------
    // Parent Supplier Dropdown
    // -------------------------
    if (selectedVendors.size > 0) {
      const parentCodes = new Set();

      selectedVendors.forEach((vendorCode) => {
        vendorRelationshipIndex.parentCodesByVendor
          .get(vendorCode)
          ?.forEach((parentCode) => parentCodes.add(parentCode));
      });

      result.parentSupplierID = vendorStaticDropdownOptions.parentSupplierID.filter(
        (option) => parentCodes.has(
          normalizeFilterValue(option.actualValue ?? option.value)
        )
      );

    } else {

      result.parentSupplierID =
        vendorStaticDropdownOptions.parentSupplierID;

    }

    return result;

  }, [
    vendorStaticDropdownOptions,
    vendorRelationshipIndex,
    deferredVendorSelectedValueSets
  ]);

  // HANDLE CHANGE
  const handleFilterChange = (key, values) => {
    setSelectedFilters((prev) => ({
      ...prev,
      [key]: values,
    }));
  };

  const handlePlantChange = (newPlant) => {
    setSelectedFilters((prev) => {
      const updated = { ...prev };

      filterConfig.forEach((item) => {
        updated[item.key] = [];
      });

      return updated;
    });

    onPlantChange(newPlant || null);
  };

  const getValues = (key) => {
    const arr = selectedFilters[key] || [];
    const allMarker = arr.find((item) => item?.__isAll);

    // Select All
    if (allMarker) {
      const optionSource = vendorFilterConfig.some(
        (item) => item.key === key
      )
        ? vendorDropdownOptions
        : dropdownOptions;

      const excludedValues = new Set(allMarker.excludedValues || []);

      return (optionSource[key] || [])
        .filter((item) => !excludedValues.has(item.value))
        .map((item) => item.actualValue ?? item.value);
    }

    return arr.map((item) => item.actualValue ?? item.value);
  };

  const onHandleExecute = async () => {
    if (executeLoading) {
      return;
    }

    const payload = {
      description: description,
      materials: getValues("tccMaterialNumber"),
      plants: selectedPlantCodes,
      mrpAreas: getValues("mrpArea"),
      mrpControllers: getValues("mrpController"),
      mrpTypes: [],
      mrpGroups: [],
      purchasingGroups: getValues("purchasingGroup"),
      // plantSpecificMaterialStatus: getValues(
      //   selectedFilters.plantSpecificMaterialStatus
      // ),
      vendor: getValues("vendorCode"),
      parentsSuppliersIds: getValues("parentSupplierID"),
      externalMaterialGroups: getValues("externalMaterialGroup"),
      supplierPin: getValues("supplierPIN"),
      userEmailTick: sendEmailToPlanner ? "X" : null,
      fromDate: selectedTimePeriodFrom?.format("YYYY-MM-DD"),
      toDate: selectedTimePeriodTo?.format("YYYY-MM-DD"),
      userName,
      userEmail,
      userRole
    };

    try {
      const response = await dispatch(
        postExtractForecastFromSAPSlice({
          dynamicEndpoint: postExtractForecastFromSAPEndpoint,
          data: payload,
          params: {
            userEmail: userEmail,
            plant: selectedPlantForPayload || "ALL",
            region: ['Global Planner', 'App Admin'].includes(userRole) ? "ALL" : (selectedRegion || "ALL")
          }
        }),
      );

      if (response?.meta?.requestStatus === "fulfilled") {
        // If parent component needs the response
        onRenderExecuteCallback(response.payload);
      }
    } catch (error) {
      console.error("Execute API error:", error);
    }
  };

  const onHandleClear = () => {
    const cleared = {};

    filterConfig?.forEach((item) => {
      cleared[item.key] = [];
    });
    vendorFilterConfig.forEach(item => {
      cleared[item.key] = [];
    });

    setSelectedFilters(cleared);
    onPlantChange(null);
    onRenderClearCallback(cleared);
  };
  const GENERAL_PARAMETER_KEYS = [
    "mrpArea",
    "tccMaterialNumber",
    "mrpController",
    "purchasingGroup",
    "plantSpecificMaterialStatus",
    "externalMaterialGroup",
    "supplierPIN",
  ];

  const VENDOR_KEYS = [
    "vendorCode",
    "parentSupplierID"
  ];

  const isExecuteDisabled =
    !hasSelectedPlant || !selectedTimePeriodFrom || !selectedTimePeriodTo;

  const SectionHeader = ({ title }) => (
    <Box
      sx={{
        backgroundColor: "#f5f5f5",
        border: "1px solid #d9d9d9",
        borderBottom: 0,
        px: 2,
        py: 1,
        mt: 2,
      }}
    >
      <Typography fontSize={14} fontWeight={600}>
        {title}
      </Typography>
    </Box>
  );

  return (
    <div>
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}>
        <div>
          <TextField
            style={{ width: 600 }}
            multiline
            maxRows={4}
            placeholder="Enter Description *"
            value={description || ""}
            onChange={(e) =>
              setDescription(e.target.value)
            }
          />
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 16,
            marginBottom: 5,
            marginRight: 30,
          }}
        >
          <ButtonComponent
            type="primary"
            maxWidth="120px"
            label="Clear"
            height="40px"
            onClick={onHandleClear}
            disabled={executeLoading}
          // disabled={!isSearchEnabled || searchTableSelector.loading}
          />
          <ButtonComponent
            type="secondary"
            maxWidth="120px"
            label={executeLoading ? "Executing..." : "Execute"}
            height="40px"
            onClick={onHandleExecute}
            disabled={isExecuteDisabled || executeLoading || !description.trim()}
          />
        </div>
      </div>
      {/* <Grid container spacing={2}>
        {filterConfig?.map((filter) => (
          <Grid
            item
            xs={12}
            sm={6}
            md={4}
            lg={4}
            p={1}
            key={filter.key}
          >
            <FilterMultiSelectExtractSAP
              label={filter.label}
              options={
                dropdownOptions[
                filter.key
                ]
              }
              value={
                selectedFilters[
                filter.key
                ]
              }
              onChange={(values) =>
                handleFilterChange(
                  filter.key,
                  values
                )
              }
            />
          </Grid>
        ))}
      </Grid> */}

      <>
        <SectionHeader title="General Parameters" />

        <Box
          sx={{
            border: "1px solid #d9d9d9",
            p: 2,
            mb: 2,
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                options={Array.isArray(plantOptions) ? plantOptions : []}
                loading={plantLoading}
                value={selectedPlant}
                onChange={(event, newValue) => handlePlantChange(newValue)}
                isOptionEqualToValue={(option, val) =>
                  option?.value === val?.value
                }
                getOptionLabel={(option) => option?.label || ""}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label="Plant *"
                    placeholder="Select Plant"
                  />
                )}
              />
            </Grid>
            {filterConfig
              .filter((f) => GENERAL_PARAMETER_KEYS.includes(f.key))
              .map((filter) => (
                <Grid item xs={12} sm={6} md={4} key={filter.key}>
                  <FilterMultiSelectExtractSAP
                    label={filter.label}
                    options={
                      hasSelectedPlant ? dropdownOptions[filter.key] : []
                    }
                    value={selectedFilters[filter.key]}
                    loading={showDependentLoader}
                    disabled={!hasSelectedPlant || showDependentLoader}
                    onChange={(values) =>
                      handleFilterChange(filter.key, values)
                    }
                  />
                </Grid>
              ))}
          </Grid>
        </Box>

        <SectionHeader title="Vendor" />

        <Box
          sx={{
            border: "1px solid #d9d9d9",
            p: 2,
          }}
        >
          <Grid container spacing={2}>
            {vendorFilterConfig
              .filter((f) => VENDOR_KEYS.includes(f.key))
              .map((filter) => (
                <Grid item xs={12} sm={6} md={4} key={filter.key}>
                  <FilterMultiSelectExtractSAP
                    label={filter.label}
                    options={vendorDropdownOptions?.[filter.key] || []}
                    value={selectedFilters[filter.key]}
                    onChange={(values) =>
                      handleFilterChange(filter.key, values)
                    }
                  />
                </Grid>
              ))}
          </Grid>
        </Box>
      </>
    </div>
  );
}
